import React, { useContext, useEffect, useState } from 'react';
import { Web3Context } from '../../context/Web3Context';
import useContract from '../../hooks/useContract';
import { shortenAddress } from '../../utils/formatters';
import { PACKAGE_NAMES } from '../../utils/constants';
import Loader from '../../components/Loader/Loader';
import './Team.css';

const Team = () => {
  const { userId } = useContext(Web3Context);
  const { getReferrals, getUserDetails, getUserPackages, getMatrixDetails, getMatrixLevelCounts, getMatrixLevelUsers } = useContract();
  const [referrals, setReferrals] = useState([]);
  const [referralDetails, setReferralDetails] = useState([]);
  const [packages, setPackages] = useState([]);
  const [teamSize, setTeamSize] = useState(0);
  const [selectedPkg, setSelectedPkg] = useState(1);
  const [levelCounts, setLevelCounts] = useState([]);
  const [expandedLevel, setExpandedLevel] = useState(null);
  const [levelUsers, setLevelUsers] = useState([]);
  const [levelPage, setLevelPage] = useState(1);
  const [refPage, setRefPage] = useState(1);
  const [levelLoading, setLevelLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const perPage = 10;

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return;
      setPageLoading(true);
      try {
        const [refs, pkgs] = await Promise.all([getReferrals(), getUserPackages()]);
        setReferrals(refs);
        setPackages(pkgs);

        const details = [];
        for (const refId of refs) {
          try { details.push(await getUserDetails(refId)); } catch { details.push({ id: refId, account: '', isActive: false, totalDirectReferrals: 0 }); }
        }
        setReferralDetails(details);

        const firstActive = pkgs.findIndex((p) => p);
        if (firstActive >= 0) {
          setSelectedPkg(firstActive + 1);
          const matrix = await getMatrixDetails(null, firstActive + 1);
          setTeamSize(matrix.totalTeamSize);
          const counts = await getMatrixLevelCounts(null, firstActive + 1);
          setLevelCounts(counts);
        }
      } catch (err) { console.error(err); }
      finally { setPageLoading(false); }
    };
    fetchData();
  }, [userId]);

  useEffect(() => {
    const loadMatrix = async () => {
      if (!userId || !packages[selectedPkg - 1]) return;
      try {
        const [matrix, counts] = await Promise.all([
          getMatrixDetails(null, selectedPkg),
          getMatrixLevelCounts(null, selectedPkg),
        ]);
        setTeamSize(matrix.totalTeamSize);
        setLevelCounts(counts);
        setExpandedLevel(null);
        setLevelUsers([]);
      } catch {}
    };
    loadMatrix();
  }, [selectedPkg]);

  const toggleLevel = async (level) => {
    if (expandedLevel === level) {
      setExpandedLevel(null);
      setLevelUsers([]);
      return;
    }
    setExpandedLevel(level);
    setLevelPage(1);
    setLevelLoading(true);
    try {
      const users = await getMatrixLevelUsers(null, selectedPkg, level);
      setLevelUsers(users);
    } catch { setLevelUsers([]); }
    finally { setLevelLoading(false); }
  };

  if (pageLoading) return <Loader text="Loading Team" />;

  const refTotal = Math.ceil(referralDetails.length / perPage);
  const refPaged = referralDetails.slice((refPage - 1) * perPage, refPage * perPage);
  const lvlTotal = Math.ceil(levelUsers.length / perPage);
  const lvlPaged = levelUsers.slice((levelPage - 1) * perPage, levelPage * perPage);

  return (
    <div className="team-page">
      <div className="page-header"><h1 className="page-title">My Team</h1></div>

      <div className="team-stats">
        <div className="ts-item"><span className="ts-value">{referrals.length}</span><span className="ts-label">Direct Referrals</span></div>
        <div className="ts-item"><span className="ts-value">{teamSize}</span><span className="ts-label">Total Team</span></div>
      </div>

      <div className="dash-card referral-section">
        <span className="ref-label">Referral Link</span>
        <div className="ref-box">
          <input type="text" readOnly value={`${window.location.origin}/register?ref=${userId}`} className="ref-input" />
          <button className="btn-copy" onClick={() => {
            navigator.clipboard.writeText(`${window.location.origin}/register?ref=${userId}`);
            const btn = document.querySelector('.team-page .btn-copy');
            if (btn) { btn.textContent = 'Copied!'; setTimeout(() => { if (btn) btn.textContent = 'Copy'; }, 2000); }
          }}>Copy</button>
        </div>
      </div>

      {/* Direct Referrals */}
      <div className="dash-card">
        <h3 className="card-title">Direct Referrals ({referrals.length})</h3>
        {referralDetails.length === 0 ? (
          <p className="empty-msg">No referrals yet</p>
        ) : (
          <>
            <div className="team-list">
              {refPaged.map((ref, i) => (
                <div key={ref.id} className="team-row">
                  <span className="tr-num">{(refPage - 1) * perPage + i + 1}</span>
                  <div className="tr-info">
                    <span className="tr-id">#{ref.id}</span>
                    <span className="tr-addr">{shortenAddress(ref.account)}</span>
                  </div>
                  <span className={`tr-status ${ref.isActive ? 'active' : ''}`}>{ref.isActive ? 'Active' : 'Inactive'}</span>
                </div>
              ))}
            </div>
            {refTotal > 1 && (
              <div className="pagination">
                <button className="pg-btn" disabled={refPage <= 1} onClick={() => setRefPage(refPage - 1)}>Prev</button>
                <span className="pg-info">{refPage} / {refTotal}</span>
                <button className="pg-btn" disabled={refPage >= refTotal} onClick={() => setRefPage(refPage + 1)}>Next</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Matrix Levels */}
      <div className="dash-card">
        <h3 className="card-title">Matrix Levels</h3>
        <div className="pkg-dropdown-wrap">
          <select className="pkg-dropdown" value={selectedPkg} onChange={(e) => setSelectedPkg(Number(e.target.value))}>
            {PACKAGE_NAMES.map((name, i) => (
              <option key={i} value={i + 1} disabled={!packages[i]}>{name} {!packages[i] ? '(Locked)' : ''}</option>
            ))}
          </select>
        </div>

        <div className="level-list">
          {levelCounts.map((count, i) => {
            const level = i + 1;
            const max = Math.pow(2, level);
            const isExpanded = expandedLevel === level;

            return (
              <div key={i} className="level-list-item">
                <button
                  className={`level-row ${isExpanded ? 'expanded' : ''} ${count > 0 ? 'has-users' : ''}`}
                  onClick={() => count > 0 && toggleLevel(level)}
                  disabled={count === 0}
                >
                  <div className="lr-left">
                    <span className="lr-level">Level {level}</span>
                    <span className="lr-count">{count} / {max} users</span>
                  </div>
                  <div className="lr-right">
                    {count > 0 && (
                      <svg className={`lr-arrow ${isExpanded ? 'open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                </button>

                {isExpanded && (
                  <div className="level-expanded">
                    {levelLoading ? (
                      <p className="level-loading">Loading...</p>
                    ) : lvlPaged.length === 0 ? (
                      <p className="empty-msg">No users</p>
                    ) : (
                      <>
                        <div className="level-users-list">
                          {lvlPaged.map((uid, j) => (
                            <div key={uid} className="lu-item">
                              <span className="lu-num">{(levelPage - 1) * perPage + j + 1}.</span>
                              <span className="lu-id">#{uid}</span>
                            </div>
                          ))}
                        </div>
                        {lvlTotal > 1 && (
                          <div className="pagination">
                            <button className="pg-btn" disabled={levelPage <= 1} onClick={() => setLevelPage(levelPage - 1)}>Prev</button>
                            <span className="pg-info">{levelPage} / {lvlTotal}</span>
                            <button className="pg-btn" disabled={levelPage >= lvlTotal} onClick={() => setLevelPage(levelPage + 1)}>Next</button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Team;
